import type { AuditActor, AuditEvent } from '@sirius/contracts/admin'
import type { Column, Row, Table } from '@tanstack/react-table'
import { ChevronDown, ChevronRight, ListFilter } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { DataTableFilterDropdown } from '~/components/refineUi/dataTable/dataTableFilter'
import { Button } from '~/components/ui/button'
import { Separator } from '~/components/ui/separator'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Table as UiTable,
} from '~/components/ui/table'
import type { GameNames } from '~/pages/cabinets/gameNames'
import {
  actorLabel,
  changeRows,
  entityName,
  entityTypeLabel,
  tenantLabel,
} from './activityFormat'
import {
  ACTOR_FIELD,
  actorColumnFilter,
  actorFilterValue,
  actorNameIn,
} from './activityLinks'

/** Opens or closes an event's "Field | From | To" table. */
export function ChangesToggle({
  expanded,
  onToggle,
}: {
  expanded: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const label = expanded
    ? t('activityLog.changes.hide')
    : t('activityLog.changes.show')
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-6"
      aria-label={label}
      title={label}
      aria-expanded={expanded}
      onClick={onToggle}
    >
      {expanded ? <ChevronDown /> : <ChevronRight />}
    </Button>
  )
}

/** Opens the row's "Field | From | To" table. */
export function ExpandToggle({ row }: { row: Row<AuditEvent> }) {
  return (
    <ChangesToggle
      expanded={row.getIsExpanded()}
      onToggle={row.getToggleExpandedHandler()}
    />
  )
}

function setActorFilter(table: Table<AuditEvent>, userId: string | undefined) {
  table.setColumnFilters((filters) => [
    ...filters.filter(({ id }) => id !== ACTOR_FIELD),
    ...(userId === undefined ? [] : [actorColumnFilter(userId)]),
  ])
}

/** Who acted: a user's name and email, else "Staff" or "Deleted user". */
export function ActorName({ actor }: { actor: AuditActor }) {
  const { t } = useTranslation()
  if (actor.kind !== 'user') {
    return <span className="text-muted-foreground">{actorLabel(actor, t)}</span>
  }
  return (
    <div
      className="min-w-0"
      title={actor.email}
    >
      <div className="truncate">{actor.name}</div>
      <div className="text-muted-foreground truncate text-xs">
        {actor.email}
      </div>
    </div>
  )
}

/**
 * Who acted. Staff get "Only this actor" on users: the log has no user
 * picker, so the filter starts from a row.
 */
export function ActorCell({
  event,
  table,
  canFilter,
}: {
  event: AuditEvent
  table: Table<AuditEvent>
  canFilter: boolean
}) {
  const { t } = useTranslation()
  const { actor } = event
  if (actor.kind !== 'user') {
    return <ActorName actor={actor} />
  }
  const filtered = actorFilterValue(table.getState().columnFilters) === actor.id
  return (
    <div className="flex items-center gap-1">
      <ActorName actor={actor} />
      {canFilter && !filtered && (
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground size-6 shrink-0"
          aria-label={t('activityLog.actorFilter.onlyThisActor')}
          title={t('activityLog.actorFilter.onlyThisActor')}
          onClick={() => setActorFilter(table, actor.id)}
        >
          <ListFilter className="!size-3" />
        </Button>
      )}
    </div>
  )
}

/**
 * The Actor header's filter (staff): shows the selected actor and Clear. The
 * name comes from the rows, which are that actor's events.
 */
export function ActorFilter({
  column,
  table,
}: {
  column: Column<AuditEvent>
  table: Table<AuditEvent>
}) {
  const { t } = useTranslation()
  const selected = actorFilterValue(table.getState().columnFilters)
  const actorName =
    selected === undefined
      ? undefined
      : actorNameIn(
          table.getCoreRowModel().rows.map(({ original }) => original),
          selected,
        )
  return (
    <DataTableFilterDropdown column={column}>
      {({ setIsOpen }) => (
        <div className="flex max-w-64 flex-col gap-2 text-sm">
          {selected === undefined ? (
            <p className="text-muted-foreground">
              {t('activityLog.actorFilter.hint')}
            </p>
          ) : (
            <p>
              <Trans
                i18nKey="activityLog.actorFilter.eventsBy"
                values={{
                  name: actorName ?? t('activityLog.actorFilter.selectedUser'),
                }}
                components={{ name: <span className="font-medium" /> }}
              />
            </p>
          )}
          <Separator />
          <Button
            variant="ghost"
            size="sm"
            disabled={selected === undefined}
            onClick={() => {
              setActorFilter(table, undefined)
              setIsOpen(false)
            }}
          >
            {t('activityLog.actorFilter.clear')}
          </Button>
        </div>
      )}
    </DataTableFilterDropdown>
  )
}

/** Entity type and name, linked when the record still exists. */
export function EntityCell({
  event,
  link,
}: {
  event: AuditEvent
  link: string | null
}) {
  const { t } = useTranslation()
  const name = entityName(event.entityLabel, t)
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-muted-foreground shrink-0 text-xs">
        {entityTypeLabel(event.entity, t)}
      </span>
      {link === null ? (
        <span className="truncate">{name}</span>
      ) : (
        <Link
          to={link}
          className="truncate underline underline-offset-4 hover:text-primary"
        >
          {name}
        </Link>
      )}
    </div>
  )
}

/** The tenant an event belongs to (platform log); "Platform" without one. */
export function TenantCell({ tenant }: { tenant: AuditEvent['tenant'] }) {
  const { t } = useTranslation()
  return (
    <span className={tenant === null ? 'text-muted-foreground' : undefined}>
      {tenantLabel(tenant, t)}
    </span>
  )
}

/** The expanded row: one line per changed field. */
export function ChangesTable({
  event,
  gameNames,
}: {
  event: AuditEvent
  /** a cabinet's games show by name (by id without it) */
  gameNames?: GameNames
}) {
  const { t } = useTranslation()
  const rows = changeRows(event.changes, t, gameNames, event.entity)
  if (rows.length === 0) {
    return (
      <p className="text-muted-foreground px-2 text-sm">
        {t('activityLog.changes.none')}
      </p>
    )
  }
  return (
    <UiTable
      aria-label={t('activityLog.changes.tableLabel')}
      className="bg-background rounded-md"
    >
      <TableHeader>
        <TableRow>
          <TableHead className="w-48">
            {t('activityLog.column.field')}
          </TableHead>
          <TableHead>{t('activityLog.column.from')}</TableHead>
          <TableHead>{t('activityLog.column.to')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(({ field, label, from, to }) => (
          <TableRow key={field}>
            <TableCell className="font-medium">{label}</TableCell>
            <TableCell className="break-all whitespace-normal">
              {from}
            </TableCell>
            <TableCell className="break-all whitespace-normal">{to}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </UiTable>
  )
}
